import { useRef } from 'react';
import { gsap, useGSAP } from './gsap';

type Options = {
  /** Selector for the steps, scoped to the stage element. */
  selector?: string;
  /** Called with the active index so the caller can render a counter. */
  onIndexChange?: (index: number) => void;
  /** Scroll distance per step, as a fraction of viewport height. */
  perStep?: number;
};

/**
 * Pins a section and advances its steps one at a time as the page scrolls:
 * each step rises in, holds, then leaves upward before the next arrives.
 *
 * Returns refs for the section (the element that pins) and the stage (the
 * element the steps live in).
 *
 * The stacked layout is applied by GSAP rather than by CSS on purpose. The
 * markup stays an ordinary vertical list, so narrow screens, reduced motion and
 * a failed bundle all render every step in normal flow — only the desktop
 * motion branch lifts them into a single stacked frame.
 */
export function useSteppedSequence<S extends HTMLElement, T extends HTMLElement>({
  selector = 'li',
  onIndexChange,
  perStep = 0.9,
}: Options = {}) {
  const section = useRef<S>(null);
  const stage = useRef<T>(null);

  useGSAP(
    () => {
      const sectionEl = section.current;
      const stageEl = stage.current;
      if (!sectionEl || !stageEl) return;

      const mm = gsap.matchMedia();

      mm.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => {
        const steps = gsap.utils.toArray<HTMLElement>(stageEl.querySelectorAll(selector));
        if (steps.length < 2) return;

        // Reserve the tallest step's height before stacking, so pinning the
        // section never changes the page's layout height.
        const tallest = Math.max(...steps.map((s) => s.offsetHeight));
        gsap.set(stageEl, { minHeight: tallest });
        gsap.set(steps, { position: 'absolute', top: 0, left: 0, width: '100%' });
        gsap.set(steps.slice(1), { autoAlpha: 0, yPercent: 45 });
        gsap.set(steps[0], { autoAlpha: 1, yPercent: 0 });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionEl,
            start: 'top top',
            end: () => `+=${window.innerHeight * perStep * steps.length}`,
            pin: true,
            anticipatePin: 1,
            scrub: 0.6,
            invalidateOnRefresh: true,
          },
        });

        steps.forEach((step, i) => {
          if (i > 0) {
            // The active index comes off the tweens rather than off scroll
            // progress: the timeline is not uniform (each step is an in-tween,
            // a hold, then an out-tween), so progress alone drifts out of sync
            // with what is actually on screen.
            tl.to(step, {
              autoAlpha: 1,
              yPercent: 0,
              duration: 0.5,
              ease: 'power2.out',
              onStart: () => onIndexChange?.(i),
              onReverseComplete: () => onIndexChange?.(i - 1),
            });
          }
          // Hold on screen, then clear out — except for the last step, which
          // stays put while the pin releases.
          if (i < steps.length - 1) {
            tl.to(
              step,
              { autoAlpha: 0, yPercent: -45, duration: 0.5, ease: 'power2.in' },
              '+=0.55',
            );
          }
        });

        return () => {
          // Undo the stacking so the flow layout comes back when the query
          // stops matching (a resize down to mobile, or unmount).
          gsap.set(steps, { clearProps: 'position,top,left,width,opacity,visibility,transform' });
          gsap.set(stageEl, { clearProps: 'minHeight' });
          onIndexChange?.(0);
        };
      });

      return () => mm.revert();
    },
    { scope: section, dependencies: [selector, perStep] },
  );

  return { section, stage };
}
